// כרטיסי ההצעות בפאנל בינה-1 (10 §1, 05): מה שמוצג למנהל במילים, לא JSON. whatChanges מה-api (tree/gate.ts whatChanges) הוא
// { summaryHe, counts: {added, changed, removed}, items: [{op: 'add'|'change'|'remove', path: 'definition:invoices.revenue', nameHe, detail?}] };
// ה-mock (וגרסאות ישנות) נושא items של {labelHe, count, names}. proposedBy מה-api: { actor: 'ai1:<email>', identityKind, conversationId }.
// תצפית 2026-10-09 (F20): המנהל ראה `counts {"added":1,…}`, מזהי קבוצות (sale/return), "weak" ו-`ai1:itsik@zbeda.com` — והעתיק את זה לשיחה.

export type ChangeItem = { op?: string; path?: string; nameHe?: string; detail?: string; labelHe?: string; count?: number; names?: string[] };
export type WhatChangesLike = { summaryHe?: unknown; counts?: unknown; items?: unknown } | null | undefined;
export type ProposedBy = string | { actor: string; identityKind?: string; conversationId?: string } | null | undefined;

/** [יחיד (עם "אחד/אחת"), רבים] */
const NOUN: Record<string, [string, string]> = {
  entity: ['ישות אחת', 'ישויות'],
  field: ['שדה אחד', 'שדות'],
  relation: ['קשר אחד', 'קשרים'],
  definition: ['הגדרה אחת', 'הגדרות'],
  import: ['הגדרת ייבוא אחת', 'הגדרות ייבוא'],
  cross_world: ['קשר אחד בין עולמות', 'קשרים בין עולמות'],
};
const VERB: Record<string, string> = { add: 'מוסיף', change: 'משנה', remove: 'מסיר' };

const items = (w: WhatChangesLike): ChangeItem[] => (w && typeof w === 'object' && Array.isArray(w.items) ? (w.items as ChangeItem[]) : []);
const kindOf = (i: ChangeItem) => (typeof i.path === 'string' ? i.path.split(':')[0]! : '');
const nounHe = (kind: string, n: number) => {
  const [one, many] = NOUN[kind] ?? [`${kind} אחד`, kind];
  return n === 1 ? one : `${n.toLocaleString('he-IL')} ${many}`;
};

/** האם ה-items בצורת ה-api (op/path) — אחרת ה-mock הישן ({labelHe,count,names}). */
export const hasApiItems = (w: WhatChangesLike) => items(w).some((i) => typeof i.op === 'string' && typeof i.path === 'string');

/** "מוסיף 2 הגדרות, משנה שדה אחד" — ממילים, לא מה-JSON של counts. ריק כשאין items בצורת ה-api. */
export function changesSentenceHe(w: WhatChangesLike): string {
  const list = items(w).filter((i) => typeof i.op === 'string' && typeof i.path === 'string');
  if (!list.length) return '';
  const by = new Map<string, number>();
  for (const i of list) {
    const k = `${i.op}|${kindOf(i)}`;
    by.set(k, (by.get(k) ?? 0) + 1);
  }
  const order = ['add', 'change', 'remove'];
  return [...by]
    .sort(([a], [b]) => order.indexOf(a.split('|')[0]!) - order.indexOf(b.split('|')[0]!))
    .map(([k, n]) => {
      const [op, kind] = k.split('|') as [string, string];
      return `${VERB[op] ?? op} ${nounHe(kind, n)}`;
    })
    .join(', ');
}

/** כותרת הכרטיס: שמות הצמתים (עד 3) — "מחזור, מספר חשבוניות ועוד 2"; בלי items — summaryHe. */
export function proposalTitleHe(w: WhatChangesLike, fallback: string): string {
  const names = [...new Set(items(w).map((i) => i.nameHe ?? i.names?.join(', ') ?? '').filter(Boolean))];
  if (names.length) return names.length <= 3 ? names.join(', ') : `${names.slice(0, 3).join(', ')} ועוד ${names.length - 3}`;
  const s = w && typeof w === 'object' && typeof w.summaryHe === 'string' ? w.summaryHe : '';
  return s || fallback;
}

/** 'ai1:itsik@zbeda.com' → 'מנהל בינה' (שם הבינה מול הלקוח); 'ai2:…' → 'אייבי'; אדם → השם/המזהה כפי שהוא. */
export function proposerHe(by: ProposedBy): string {
  const actor = typeof by === 'string' ? by : by?.actor ?? '';
  if (!actor) return '';
  const m = /^ai([12])(?:[^a-z0-9]|$)/.exec(actor);
  if (m) return m[1] === '1' ? 'מנהל בינה' : 'אייבי';
  if (/^בינה-?[12]/.test(actor)) return actor === 'בינה-1' ? 'מנהל בינה' : 'אייבי';
  return actor;
}

// ראיות (rationaleHe של bootstrap, template-priority documents.ts): "A·D → sale (weak; 3159478 שורות) · C·C → return …"
const GROUP_HE: Record<string, string> = {
  sale: 'מכירה', sales: 'מכירות', return: 'זיכוי/החזרה', returns: 'זיכויים', receipt: 'קבלה', receipts: 'קבלות', purchase: 'רכש', purchases: 'רכש',
  supplier_credit: 'זיכוי ספק', supplier: 'ספק', quote: 'הצעת מחיר', order: 'הזמנה', orders: 'הזמנות', proforma: 'פרופורמה', payment: 'תשלום',
  credit: 'זיכוי', debit: 'חיוב', internal: 'פנימי', unknown: 'לא מסווג', other: 'אחר', ignore: 'לא נספר',
};
const CONFIDENCE_HE: Record<string, string> = { weak: 'ודאות נמוכה', medium: 'ודאות בינונית', strong: 'ודאות גבוהה', high: 'ודאות גבוהה', low: 'ודאות נמוכה' };

/** מזהי קבוצות ודרגות ודאות באנגלית → עברית; השאר (קודים, מספרים) נשאר. */
export function humanizeEvidenceHe(text: string | null | undefined): string {
  if (!text) return '';
  return text
    .replace(/→\s*([a-z_]+)/g, (_m, g: string) => `→ ${GROUP_HE[g] ?? g}`)
    .replace(/\((weak|medium|strong|high|low)\b/g, (_m, c: string) => `(${CONFIDENCE_HE[c] ?? c}`)
    .replace(/\b(weak|strong)\b/g, (m) => CONFIDENCE_HE[m] ?? m);
}

/** תקציר ראיות לכרטיס: שורה אחת, עד ~140 תווים; המלא — ב"פרטים". */
export function evidenceSummaryHe(text: string | null | undefined, max = 140): string {
  const t = humanizeEvidenceHe(text).replace(/\s+/g, ' ').trim();
  return t.length > max ? `${t.slice(0, max - 1).trimEnd()}…` : t;
}
